/**
 * Program-facts grid shared by level and school marketing pages.
 *
 * What: Renders a visible fact grid and a compact reviewer/update block.
 * Why: Gives search engines and AI systems concise, sourceable program details
 * that match the rendered page. (The homepage carries its facts in
 * `LandingTrustSection`.)
 * When: Update when the shared fact-grid layout changes.
 */
import React from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import LandingCenteredSectionHead from '@/components/landing/LandingCenteredSectionHead';
import LandingSection from '@/components/landing/LandingSection';
import LandingSectionHeading from '@/components/landing/LandingSectionHeading';
import type { LandingSectionTone } from '@/components/landing/LandingSectionShell';
import { colors, radii, shadows } from '@/components/landing/landingTokens';

export interface ProgramFactsContent {
  eyebrow: string;
  title: string;
  body: string;
  facts: Array<{ label: string; value: string; detail?: string }>;
  reviewMeta?: {
    reviewedBy?: string;
    reviewedLabel?: string;
    updatedLabel?: string;
  };
}

interface LandingProgramFactsSectionProps {
  id: string;
  tone?: LandingSectionTone;
  content: ProgramFactsContent;
  /** 'center' matches the homepage design; 'start' keeps the school layout. */
  align?: 'start' | 'center';
}

function LandingProgramFactsSection({
  content,
  id,
  tone = 'muted',
  align = 'start',
}: LandingProgramFactsSectionProps) {
  const resolvedContent = content;
  const titleId = `${id}-title`;
  const centered = align === 'center';

  const reviewMetaPill = resolvedContent.reviewMeta ? (
    <Box
      sx={{
        display: 'inline-flex',
        alignSelf: centered ? 'center' : 'flex-start',
        flexWrap: 'wrap',
        gap: 1,
        px: 2,
        py: 1,
        borderRadius: radii.pill,
        backgroundColor: alpha(colors.accentOrange, 0.08),
        border: `1px solid ${alpha(colors.accentOrange, 0.22)}`,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: colors.ink,
          fontWeight: 700,
          fontSize: { xs: 13, sm: 14 },
        }}
      >
        {resolvedContent.reviewMeta.reviewedLabel}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: colors.inkMuted,
          fontSize: { xs: 13, sm: 14 },
        }}
      >
        {resolvedContent.reviewMeta.updatedLabel}
      </Typography>
    </Box>
  ) : null;

  return (
    <LandingSection id={id} tone={tone} ariaLabelledby={titleId}>
      <Stack spacing={centered ? 0 : { xs: 3.5, sm: 4, md: 5 }}>
        {centered ? (
          <LandingCenteredSectionHead
            titleId={titleId}
            eyebrow={resolvedContent.eyebrow}
            title={resolvedContent.title}
            body={resolvedContent.body}
            trailing={reviewMetaPill}
          />
        ) : (
          <LandingSectionHeading
            titleId={titleId}
            eyebrow={resolvedContent.eyebrow}
            title={resolvedContent.title}
            body={resolvedContent.body}
            trailing={reviewMetaPill}
          />
        )}

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: { xs: 2.25, sm: 2.5, md: 3 },
            width: '100%',
          }}
        >
          {resolvedContent.facts.map(fact => (
            <Paper
              key={fact.label}
              elevation={0}
              component="article"
              sx={{
                flex: '1 1 220px',
                minWidth: 0,
                p: { xs: 2.5, sm: 3 },
                borderRadius: radii.md,
                backgroundColor: colors.paper,
                border: `1px solid ${colors.border}`,
                boxShadow: shadows.soft,
              }}
            >
              <Stack spacing={1}>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.accentOrange,
                    fontWeight: 800,
                    letterSpacing: 1.2,
                    fontSize: 11,
                  }}
                >
                  {fact.label}
                </Typography>
                <Typography
                  component="h3"
                  sx={{
                    color: colors.ink,
                    fontWeight: 800,
                    fontSize: { xs: 17, sm: 18 },
                    lineHeight: 1.3,
                  }}
                >
                  {fact.value}
                </Typography>
                {fact.detail ? (
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.inkMuted,
                      lineHeight: 1.65,
                      fontSize: { xs: 14, sm: 15 },
                    }}
                  >
                    {fact.detail}
                  </Typography>
                ) : null}
              </Stack>
            </Paper>
          ))}
        </Box>
      </Stack>
    </LandingSection>
  );
}

export default LandingProgramFactsSection;
